import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  MenuItem,
  InputAdornment
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
// import TargetIcon from '@mui/icons-material/Target';  // double-check if you're using the right icon name
import InfoIcon from '@mui/icons-material/Info';
import ReceiptIcon from '@mui/icons-material/ReceiptLong';

import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import FormHelperText from '@mui/material/FormHelperText';


const CompanyDetailsForm = ({ companyData, errors, onChange }) => {
  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501+ employees',
  ];

  const industries = [
    'Technology',
    'Finance',
    'Healthcare',
    'Education',
    'Retail',
    'Manufacturing',
    'Real Estate',
    'Construction',
    'Transportation',
    'Entertainment',
    'Other'
  ];

  const countries = [
    'Australia',
    'Canada',
    'India',
    'United Arab Emirates',
    'United Kingdom',
    'United States',
    'Other'
  ];

  const primaryFocusOptions = ['B2B (Business To Business)', 'B2C (Business To Consumer)', 'Both B2B and B2C'];
  const hearAboutUsOptions = ['Search Engine', 'Social Media', 'Friends/College', 'Advertisement'];


  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Company Details
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Please provide information about your company.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="companyName"
            label="Company Name"
            name="companyName"
            value={companyData.companyName}
            onChange={(e) => onChange('companyName', e.target.value)}
            error={!!errors.companyName}
            helperText={errors.companyName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            select
            id="industry"
            label="Industry"
            name="industry"
            value={companyData.industry}
            onChange={(e) => onChange('industry', e.target.value)}
            error={!!errors.industry}
            helperText={errors.industry}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CategoryIcon color="action" />
                </InputAdornment>
              ),
            }}
          >
            {industries.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            select
            id="size"
            label="Company Size"
            name="size"
            value={companyData.size}
            onChange={(e) => onChange('size', e.target.value)}
            error={!!errors.size}
            helperText={errors.size}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GroupsIcon color="action" />
                </InputAdornment>
              ),
            }}
          >
            {companySizes.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="website"
            label="Company Website"
            name="website"
            value={companyData.website}
            error={!!errors.website}
            helperText={errors.website}
            onChange={(e) => onChange('website', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LanguageIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            select
            id="country"
            label="Company Location"
            name="country"
            value={companyData.country}
            onChange={(e) => onChange('country', e.target.value)}
            error={!!errors.country}
            helperText={errors.country}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PublicIcon color="action" />
                </InputAdornment>
              ),
            }}
          >
            {countries.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            // Only required for India
            // required={companyData.country === 'India'}
            fullWidth
            id="gst"
            label={`GST${companyData.country === 'India' ? ' (optional)' : ''}`}
            name="gst"
            value={companyData.gst}
            onChange={(e) => onChange('gst', e.target.value)}
            error={!!errors.gst}
            helperText={errors.gst}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ReceiptIcon color="action" />
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 15 }}
            // Disable the field when country is not India
            disabled={companyData.country !== 'India'}
          />
        </Grid>
        
        <Grid item xs={12}>
          <FormControl component="fieldset" error={!!errors.primaryFocus} sx={{ mt: 1 }}>
            <FormLabel component="legend">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupsIcon color="action" sx={{ mr: 1 }} />
                Primary Focus *
              </Box>
            </FormLabel>
            <RadioGroup
              row
              name="primaryFocus"
              value={companyData.primaryFocus}
              onChange={(e) => onChange('primaryFocus', e.target.value)}
            >
              {primaryFocusOptions.map((option) => (
                <FormControlLabel 
                  key={option} 
                  value={option} 
                  control={<Radio />} 
                  label={option} 
                />
              ))}
            </RadioGroup>
            {errors.primaryFocus && <FormHelperText>{errors.primaryFocus}</FormHelperText>}
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <FormControl component="fieldset" error={!!errors.hearAboutUs} sx={{ mt: 1 }}>
            <FormLabel component="legend">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon color="action" sx={{ mr: 1 }} />
                How did you hear about us? *
              </Box>
            </FormLabel>
            <RadioGroup
              row
              name="hearAboutUs"
              value={companyData.hearAboutUs}
              onChange={(e) => onChange('hearAboutUs', e.target.value)}
            >
              {hearAboutUsOptions.map((option) => (
                <FormControlLabel 
                  key={option} 
                  value={option} 
                  control={<Radio />} 
                  label={option} 
                />
              ))}
            </RadioGroup>
            {errors.hearAboutUs && <FormHelperText>{errors.hearAboutUs}</FormHelperText>}
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyDetailsForm;